import type { FetchOptions, ApiResponse, ApiError } from '@/shared/model/types/http';

export const useApi = () => {
  const config = useRuntimeConfig();
  const baseURL = config.public.apiUrl;
  const accessToken = useCookie('ECOM_ACCESS_TOKEN', { path: '/', maxAge: 900 });
  const refreshToken = useCookie('ECOM_REFRESH_TOKEN', { path: '/', maxAge: 604800 });

  const fetchHttp = async <T>(
    url: string,
    options: FetchOptions = {},
    isAuth: boolean = false,
  ): Promise<ApiResponse<T>> => {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept-Language': useCookie('ECOM_LANGUAGE', { path: '/' }).value || 'ru',
        ...options.headers,
      };

      if (isAuth && accessToken.value) {
        headers.Authorization = `Bearer ${accessToken.value}`;
      }

      let finalUrl = `${baseURL}${url}`;

      if (options.params) {
        const queryParams = new URLSearchParams();

        Object.entries(options.params).forEach(([key, value]) => {
          if (value == null) return;

          if (Array.isArray(value)) {
            value.forEach(v => queryParams.append(key, String(v)));
          } else {
            queryParams.append(key, String(value));
          }
        });

        const queryString = queryParams.toString();
        if (queryString) {
          finalUrl += `?${queryString}`;
        }
      }

      const response: any = await $fetch(finalUrl, {
        method: options.method || 'GET',
        headers,
        body: options.body ? JSON.stringify(options.body) : undefined,
        onResponse({ response }) {
          if (response.status === 204) {
            response._data = {};
          }
        },
      });

      if (response.access && response.refresh) {
        accessToken.value = response.access;
        refreshToken.value = response.refresh;
      }

      return {
        data: response,
        status: response.status,
      };
    } catch (error: any) {
      const status = error.response?.status || 500;

      if (status === 401 && refreshToken.value && isAuth) {
        try {
          const tokenResponse: any = await $fetch(`${baseURL}/auth/refresh-token/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              refresh: refreshToken.value,
            }),
          });

          if (tokenResponse.access && tokenResponse.refresh) {
            accessToken.value = tokenResponse.access;
            refreshToken.value = tokenResponse.refresh;

            return fetchHttp<T>(url, options, isAuth);
          } else {
            throw new Error('Неверный ответ при обновлении токенов');
          }
        } catch (refreshError) {
          accessToken.value = null;
          refreshToken.value = null;
          throw new Error('Не удалось обновить токены');
        }
      }

      const apiError: ApiError = {
        status: status,
        response: error.response || {},
      };

      throw apiError;
    }
  };

  return {
    get: <T>(url: string, isAuth: boolean, options?: FetchOptions) =>
      fetchHttp<T>(url, { method: 'GET', ...options }, isAuth),
    post: <T>(url: string, isAuth: boolean, body?: any, options?: FetchOptions) =>
      fetchHttp<T>(url, { method: 'POST', body, ...options }, isAuth),
    put: <T>(url: string, body: any, isAuth: boolean, options?: FetchOptions) =>
      fetchHttp<T>(url, { method: 'PUT', body, ...options }, isAuth),
    patch: <T>(url: string, body: any, isAuth: boolean, options?: FetchOptions) =>
      fetchHttp<T>(url, { method: 'PATCH', body, ...options }, isAuth),
    delete: <T>(url: string, isAuth: boolean, options?: FetchOptions) =>
      fetchHttp<T>(url, { method: 'DELETE', ...options }, isAuth),
  };
};
